import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import BusModifySearch from "./ModifyBusSearch";
import { RiBusLine, RiFilterLine } from "react-icons/ri";
import "./BusResults.css";
import DefaultData from "../../CompanyData/DefaultData";

const BusResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const initialSearchQuery = location.state?.searchQuery;

  const [currentSearch, setCurrentSearch] = useState(initialSearchQuery);
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    busTypes: [],
    operators: [],
    priceRange: [0, 10000],
    amenities: [],
    rtc: false,
  });

  const [filteredBuses, setFilteredBuses] = useState([]);

  // API call to search buses
  const searchBuses = async (searchData) => {
    setLoading(true);
    setError(null);

    try {
      const requestData = {
        CompanyId: DefaultData?.DEFAULTCOMPANYID || "00000000-0000-0000-0000-000000000000",
        Source: searchData.fromStation,
        Destination: searchData.toStation,
        DateOfJourney: searchData.travelDate,
        Passengers: 1,
        ReturnDate: null,
      };

      const response = await apiService.post(config.SEARCHFBUSES, requestData);

      if (response && Array.isArray(response)) {
        setBuses(response);
        setFilteredBuses(response);
      } else {
        setBuses([]);
        setFilteredBuses([]);
      }
    } catch (err) {
      setError("Failed to fetch bus data. Please try again.");
      setBuses([]);
      setFilteredBuses([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle seat selection
  const handleSelectSeats = (bus) => {
    navigate("/bus/seats", {
      state: {
        bus: bus,
        searchQuery: currentSearch,
      },
    });
  };

  // Format date to "22-Nov 2025" format
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString("en", { month: "short" });
      const year = date.getFullYear();

      return `${day}-${month} ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Initial search when component mounts
  useEffect(() => {
    if (currentSearch) {
      searchBuses(currentSearch);
    }
  }, []);

  // Handle search from BusModifySearch component
  const handleModifySearch = (newSearchQuery) => {
    setCurrentSearch(newSearchQuery);
    searchBuses(newSearchQuery);
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...buses];

    if (filters.busTypes.length > 0) {
      filtered = filtered.filter((bus) =>
        filters.busTypes.includes(bus.busType)
      );
    }

    if (filters.operators.length > 0) {
      filtered = filtered.filter((bus) =>
        filters.operators.includes(bus.operatorName)
      );
    }

    const minPrice = parseFloat(filters.priceRange[0]);
    const maxPrice = parseFloat(filters.priceRange[1]);
    filtered = filtered.filter((bus) => {
      const basePrice = getBasePrice(bus);
      return basePrice >= minPrice && basePrice <= maxPrice;
    });

    if (filters.amenities.length > 0) {
      filtered = filtered.filter((bus) => {
        const busAmenities = bus.busAmenities || [];
        return filters.amenities.every((amenity) =>
          busAmenities.includes(amenity)
        );
      });
    }

    if (filters.rtc) {
      filtered = filtered.filter((bus) => bus.rtc === true);
    }

    setFilteredBuses(filtered);
  }, [filters, buses]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter((item) => item !== value)
        : [...prev[filterType], value],
    }));
  };

  const handleRTCFilterChange = (checked) => {
    setFilters((prev) => ({
      ...prev,
      rtc: checked,
    }));
  };

  const handlePriceRangeChange = (min, max) => {
    setFilters((prev) => ({
      ...prev,
      priceRange: [min, max],
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      busTypes: [],
      operators: [],
      priceRange: [0, 10000],
      amenities: [],
      rtc: false,
    });
  };

  const getBusTypeBadge = (busType) => {
    const type = busType?.toLowerCase() || "seater";
    const typeConfig = {
      sleeper: { label: "Sleeper", class: "bus-type-badge-sleeper" },
      "semi-sleeper": {
        label: "Semi Sleeper",
        class: "bus-type-badge-semi-sleeper",
      },
      seater: { label: "Seater", class: "bus-type-badge-seater" },
      luxury: { label: "Luxury", class: "bus-type-badge-luxury" },
    };

    const config = typeConfig[type] || {
      label: busType || "Bus",
      class: "bus-type-badge-default",
    };
    return (
      <span className={`bus-type-badge ${config.class}`}>{config.label}</span>
    );
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getBasePrice = (bus) => {
    if (!bus.basePrice) return 0;

    try {
      let price;
      if (typeof bus.basePrice === "string") {
        const prices = bus.basePrice.split(",");
        price = parseFloat(prices[0]);
      } else {
        price = parseFloat(bus.basePrice);
      }

      return isNaN(price) ? 0 : Math.round(price);
    } catch (error) {
      return 0;
    }
  };

  // Get unique amenities from all buses
  const getAllUniqueAmenities = () => {
    const allAmenities = new Set();
    buses.forEach((bus) => {
      if (bus.busAmenities && Array.isArray(bus.busAmenities)) {
        bus.busAmenities.forEach((amenity) => allAmenities.add(amenity));
      }
    });
    return Array.from(allAmenities);
  };

  // Render amenities for a bus
  const renderBusAmenities = (bus) => {
    const amenities = bus.busAmenities || [];
    const displayedAmenities = amenities.slice(0, 3);
    const remainingCount = amenities.length - 3;

    return (
      <div className="bus-amenities-display">
        {displayedAmenities.map((amenity, index) => (
          <span key={index} className="bus-amenity-tag" title={amenity}>
            {amenity}
          </span>
        ))}
        {remainingCount > 0 && (
          <span
            className="bus-amenity-more"
            title={amenities.slice(3).join(", ")}
          >
            +{remainingCount} more
          </span>
        )}
      </div>
    );
  };

  if (!currentSearch) {
    return (
      <div id="layout-wrapper">
        <div className="main-content">
          <div className="container-fluid">
            <div className="alert alert-danger">
              No search data found. Please search again.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="layout-wrapper">
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card pt-0">
              <BusModifySearch
                searchQuery={currentSearch}
                onSearch={handleModifySearch}
              />

              <div className="card-body">
                {loading && (
                  <div className="text-center py-5">
                    <div className="bus-spinner"></div>
                    <p>Searching for buses...</p>
                  </div>
                )}

                {error && (
                  <div className="alert alert-danger text-center">{error}</div>
                )}

                {!loading && !error && (
                  <div className="row">
                    {/* Filters Sidebar */}
                    <div className="col-lg-3">
                      <div className="card bus-filters-card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">
                            <RiFilterLine className="me-2" />
                            Filters
                          </h6>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={clearAllFilters}
                          >
                            Clear All
                          </button>
                        </div>
                        <div className="card-body">
                          {/* RTC Filter */}
                          <div className="bus-filter-section">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={filters.rtc}
                                onChange={(e) =>
                                  handleRTCFilterChange(e.target.checked)
                                }
                                id="rtc-filter"
                              />
                              <label
                                className="form-check-label d-flex justify-content-between w-100"
                                htmlFor="rtc-filter"
                              >
                                RTC
                              </label>
                            </div>
                          </div>

                          {/* Bus Type Filter */}
                          <div className="bus-filter-section">
                            <h6 className="bus-filter-title">Bus Type</h6>
                            {Array.from(
                              new Set(
                                buses.map((bus) => bus.busType).filter(Boolean)
                              )
                            ).map((type) => (
                              <div key={type} className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={filters.busTypes.includes(type)}
                                  onChange={() =>
                                    handleFilterChange("busTypes", type)
                                  }
                                  id={`bus-type-${type}`}
                                />
                                <label
                                  className="form-check-label d-flex justify-content-between w-100"
                                  htmlFor={`bus-type-${type}`}
                                >
                                  {type}
                                </label>
                              </div>
                            ))}
                          </div>

                          {/* Amenities Filter */}
                          <div className="bus-filter-section">
                            <h6 className="bus-filter-title">Amenities</h6>
                            {getAllUniqueAmenities().map((amenity) => (
                              <div key={amenity} className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={filters.amenities.includes(amenity)}
                                  onChange={() =>
                                    handleFilterChange("amenities", amenity)
                                  }
                                  id={`amenity-${amenity}`}
                                />
                                <label
                                  className="form-check-label w-100"
                                  htmlFor={`amenity-${amenity}`}
                                >
                                  {amenity}
                                </label>
                              </div>
                            ))}
                          </div>

                          {/* Price Range Filter */}
                          <div className="bus-filter-section">
                            <h6 className="bus-filter-title">Price Range</h6>
                            <div className="bus-price-range">
                              <input
                                type="range"
                                className="form-range"
                                min="0"
                                max="10000"
                                step="100"
                                value={filters.priceRange[1]}
                                onChange={(e) =>
                                  handlePriceRangeChange(
                                    filters.priceRange[0],
                                    parseInt(e.target.value)
                                  )
                                }
                              />
                              <div className="d-flex justify-content-between">
                                <small>INR {filters.priceRange[0]}</small>
                                <small>INR {filters.priceRange[1]}</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bus List */}
                    <div className="col-lg-9">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                          Available Buses ({filteredBuses.length})
                          <small className="text-muted ms-2">
                            {currentSearch.fromStation} to{" "}
                            {currentSearch.toStation} •{" "}
                            {formatDisplayDate(currentSearch.travelDate)}
                          </small>
                        </h5>
                      </div>

                      {filteredBuses.length === 0 ? (
                        <div className="text-center py-5">
                          <RiBusLine size={48} className="text-muted mb-3" />
                          <h5>No buses found</h5>
                          <p className="text-muted">
                            Try modifying your search or clearing filters
                          </p>
                        </div>
                      ) : (
                        filteredBuses.map((bus, index) => (
                          <div
                            key={bus.routeScheduleId || `bus-${index}`}
                            className="card mb-3 bus-card"
                          >
                            <div className="card-body">
                              <div className="row align-items-center">
                                <div className="col-md-2">
                                  <div className="text-center">
                                    <RiBusLine
                                      size={30}
                                      className="text-primary mb-2"
                                    />
                                    <h6 className="mb-1">
                                      {bus.departureTime || "N/A"}
                                    </h6>
                                    <small className="text-muted">
                                      {formatDuration(bus.durationInMins)}
                                    </small>
                                    <h6 className="mb-0 mt-1">
                                      {bus.arrivalTime || "N/A"}
                                    </h6>
                                  </div>
                                </div>

                                <div className="col-md-4">
                                  <h6 className="mb-1">
                                    {bus.operatorName || "Unknown Operator"}
                                  </h6>
                                  <div className="mb-1">
                                    {getBusTypeBadge(bus.busType)}
                                    {bus.rtc && (
                                      <span className="badge bg-warning text-dark ms-1">
                                        RTC
                                      </span>
                                    )}
                                  </div>
                                  {/* Amenities Display */}
                                  {bus.busAmenities &&
                                    bus.busAmenities.length > 0 && (
                                      <div className="mt-2">
                                        {renderBusAmenities(bus)}
                                      </div>
                                    )}
                                </div>

                                <div className="col-md-2">
                                  <div className="mb-2">
                                    <small
                                      className={`badge ${
                                        (bus.availableSeats || 0) > 10
                                          ? "bg-success"
                                          : (bus.availableSeats || 0) > 5
                                          ? "bg-warning"
                                          : "bg-danger"
                                      }`}
                                    >
                                      {bus.availableSeats || 0} seats available
                                    </small>
                                  </div>
                                </div>

                                <div className="col-md-2 text-center">
                                  <h5 className="text-primary mb-1">
                                    INR {getBasePrice(bus)}
                                  </h5>
                                  <small className="text-muted">per seat</small>
                                </div>

                                <div className="col-md-2">
                                  <button
                                    className="btn btn-primary w-100"
                                    disabled={(bus.availableSeats || 0) === 0}
                                    onClick={() => handleSelectSeats(bus)}
                                  >
                                    {(bus.availableSeats || 0) === 0
                                      ? "Sold Out"
                                      : "Select Seats"}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusResults;
